import axios from "axios";

const BACKEND_URL = window.location.hostname === "localhost" ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Same-origin API: works on any domain (custom domain, emergent.host, preview).
const IS_DEV = /localhost|preview\.emergentagent\.com/.test(window.location.hostname);
export const PUBLIC_BASE = IS_DEV ? BACKEND_URL : (process.env.REACT_APP_PUBLIC_BASE || BACKEND_URL);
export const PUBLIC_API = `${PUBLIC_BASE}/api`;

const http = axios.create({ baseURL: API, withCredentials: true });
const pub = axios.create({ baseURL: API, withCredentials: true });

let WORKSPACE = "main";
export const setWorkspace = (ws) => { WORKSPACE = ws; };
export const getWorkspace = () => WORKSPACE;
const addWorkspace = (cfg) => { if (WORKSPACE === "private") cfg.headers["X-Workspace"] = "private"; return cfg; };
http.interceptors.request.use(addWorkspace);
pub.interceptors.request.use(addWorkspace);

const onUnauthorized = (err) => {
  const url = err?.config?.url || "";
  if (err?.response?.data?.code === "private_locked") { window.location.replace("/"); return Promise.reject(err); }
  if (err?.response?.status === 401 && !url.startsWith("/auth/") && window.location.pathname !== "/admin-login") {
    window.location.replace("/admin-login");
  }
  return Promise.reject(err);
};
http.interceptors.response.use((r) => r, onUnauthorized);
pub.interceptors.response.use((r) => r, onUnauthorized);
export const unlockPrivate = (password) => http.post("/auth/private/unlock", { password }).then((r) => r.data);
export const lockPrivate = () => http.post("/auth/private/lock").then((r) => r.data);

export const authMe = () => http.get("/auth/me").then((r) => r.data);
export const authRequestOtp = (identifier) => http.post("/auth/request-otp", { identifier }).then((r) => r.data);
export const authVerifyOtp = (challenge, code) => http.post("/auth/verify-otp", { challenge, code }).then((r) => r.data);
export const authLogout = () => http.post("/auth/logout").then((r) => r.data);
export const authActivity = () => http.get("/auth/activity").then((r) => r.data);

export const listFoods = () => http.get("/foods").then((r) => r.data);
export const createFood = (food) => http.post("/foods", food).then((r) => r.data);
export const deleteFood = (id) => http.delete(`/foods/${id}`).then((r) => r.data);

export const listPatients = () => http.get("/patients").then((r) => r.data);
export const getPatient = (id) => http.get(`/patients/${id}`).then((r) => r.data);
export const savePatient = (p) => http.post("/patients", p).then((r) => r.data);
export const deletePatient = (id) => http.delete(`/patients/${id}`).then((r) => r.data);

export const listAppointments = () => http.get("/appointments").then((r) => r.data);
export const createAppointment = (a) => http.post("/appointments", a).then((r) => r.data);
export const deleteAppointment = (id) => http.delete(`/appointments/${id}`).then((r) => r.data);

export const calendarStatus = () => http.get("/oauth/calendar/status").then((r) => r.data);
export const calendarLoginUrl = () => http.get("/oauth/calendar/login").then((r) => r.data);
export const calendarDisconnect = () => http.post("/oauth/calendar/disconnect").then((r) => r.data);
export const calendarConflicts = (date, time, duration = 60) =>
  http.get("/calendar/conflicts", { params: { date, time, duration } }).then((r) => r.data);

export const shareWorkout = (patientName, allenamento, id) =>
  pub.post("/workout/share", { patientName, allenamento, id }).then((r) => r.data);
export const getWorkoutProgress = (wid) => pub.get(`/workout/${wid}/progress`).then((r) => r.data);
export const listPendingProgress = () => pub.get("/workouts/pending-progress").then((r) => r.data);

export const listOffers = () => pub.get("/offers").then((r) => r.data);
export const createOffer = (o) => pub.post("/offers", o).then((r) => r.data);
export const updateOffer = (id, o) => pub.put(`/offers/${id}`, o).then((r) => r.data);
export const setOfferActive = (id, active) => pub.patch(`/offers/${id}/active`, { active }).then((r) => r.data);
export const deleteOffer = (id) => pub.delete(`/offers/${id}`).then((r) => r.data);
export const uploadOfferImage = (blob, onProgress) => {
  const fd = new FormData();
  fd.append("file", blob, "offerta.jpg");
  return pub.post("/offers/upload", fd, { onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)) }).then((r) => r.data);
};
export const offerImageUrl = (path) => `${API}/offers/image/${path}`;
export const offerPublicUrl = (slug) => `${PUBLIC_API}/offerta/${slug}`;
export const saveWorkoutProgress = (wid, progress) => pub.put(`/workout/${wid}/progress`, { progress }).then((r) => r.data);

export const listExerciseLibrary = () => http.get("/exercise-library").then((r) => r.data);
export const saveExercise = (e) => http.post("/exercise-library", e).then((r) => r.data);
export const deleteExercise = (id) => http.delete(`/exercise-library/${id}`).then((r) => r.data);

export const getPatientLink = (pid) => http.get(`/portal/link/${pid}`).then((r) => r.data);
export const createPatientLink = (pid) => http.post(`/portal/link/${pid}`).then((r) => r.data);
export const revokePatientLink = (pid) => http.delete(`/portal/link/${pid}`).then((r) => r.data);
export const patientLinkUrl = (path) => `${PUBLIC_BASE}${path}`;
export const adminPatientVideos = (pid) => http.get(`/portal/admin/videos/${pid}`).then((r) => r.data);
export const saveVideoCorrection = (vid, body) => http.put(`/portal/admin/video/${vid}/correction`, body).then((r) => r.data);
export const deleteCorrectionVideo = (vid) => http.delete(`/portal/admin/video/${vid}/correction-video`).then((r) => r.data);
export const deletePortalVideo = (vid) => http.delete(`/portal/admin/video/${vid}`).then((r) => r.data);
export const uploadVideoFrame = (vid, blob) => {
  const fd = new FormData();
  fd.append("file", blob, "frame.jpg");
  return http.post(`/portal/admin/video/${vid}/frame`, fd).then((r) => r.data);
};
export const portalFileUrl = (vid, kind) => `${API}/portal/file/${vid}/${kind}`;
export const portalFrameUrl = (vid, fid) => `${API}/portal/frame/${vid}/${fid}`;

const portalHttp = axios.create({ baseURL: API, withCredentials: true });
export const portalMe = () => portalHttp.get("/portal/me", { params: { t: Date.now() } }).then((r) => r.data);
export const uploadPortalVideo = async (file, meta, onProgress) => {
  const { data: init } = await portalHttp.post("/portal/upload/init", { content_type: file.type, size: file.size, ...meta });
  const cs = init.chunk_size;
  for (let i = 0, idx = 0; i < file.size; i += cs, idx += 1) {
    await portalHttp.put(`/portal/upload/${init.upload_id}/chunk/${idx}`, file.slice(i, i + cs), { headers: { "Content-Type": "application/octet-stream" } });
    onProgress && onProgress(Math.min(99, Math.round(((i + cs) / file.size) * 100)));
  }
  const { data } = await portalHttp.post(`/portal/upload/${init.upload_id}/finish`);
  onProgress && onProgress(100);
  return data;
};
export const portalOpenOffer = (sid) => portalHttp.post(`/portal/offers/${sid}/open`).then((r) => r.data);
export const sendOffer = (oid, patientIds) => http.post(`/offers/${oid}/send`, { patient_ids: patientIds }).then((r) => r.data);
export const listOfferSends = (oid) => http.get(`/offers/${oid}/sends`).then((r) => r.data);
export const unsendOffer = (oid, pid) => http.delete(`/offers/${oid}/sends/${pid}`).then((r) => r.data);
export const listPendingVideos = () => http.get("/portal/admin/pending-videos").then((r) => r.data);
export const listPendingComments = () => http.get("/workouts/pending-comments").then((r) => r.data);
export const markCommentsSeen = (wid) => http.post(`/workout/${wid}/comments/seen`).then((r) => r.data);
export const listBroadcasts = () => http.get("/chat/broadcasts").then((r) => r.data);
export const createBroadcast = (body) => http.post("/chat/broadcasts", body).then((r) => r.data);
export const markBroadcastOpened = (bid, pid) => http.post(`/chat/broadcasts/${bid}/opened/${pid}`).then((r) => r.data);
export const deleteBroadcast = (bid) => http.delete(`/chat/broadcasts/${bid}`).then((r) => r.data);
export const portalSendMeasurements = (body) => portalHttp.post("/portal/measurements", body).then((r) => r.data);
export const listPendingMeasurements = () => http.get("/portal/admin/pending-measurements").then((r) => r.data);
export const confirmMeasurement = (mid) => http.post(`/portal/admin/measurements/${mid}/confirm`).then((r) => r.data);
export const listBackups = () => http.get("/backups").then((r) => r.data);
export const createBackup = () => http.post("/backups").then((r) => r.data);
export const downloadBackup = (bid) => http.get(`/backups/${bid}/download`, { responseType: "blob" });
export const uploadBackup = (file) => { const fd = new FormData(); fd.append("file", file); return http.post("/backups/upload", fd).then((r) => r.data); };
export const backupPatients = (bid) => http.get(`/backups/${bid}/patients`).then((r) => r.data);
export const restoreFromBackup = (bid, patient_ids) => http.post(`/backups/${bid}/restore`, { patient_ids }).then((r) => r.data);
